import { Fragment, useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  CheckBox,
  CheckBoxOutlineBlank,
  ExpandLess,
  ExpandMore,
  ExpandMoreRounded,
  SortRounded,
} from "@mui/icons-material";
import { AxiosResponse } from "axios";
import { useParams } from "react-router-dom";
import http from "../../utils/http";
import { computeSelectedState } from "../../utils/features";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import {
  NoDataContainer,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../Common/styles/table";
import { filterBox, SearchBox } from "../Organisations/Organisation";
import FeaturesFilterModal from "./FeaturesFilterModal";

type Props = {
  canEdit: boolean;
};
const PlatformFeaturesList = ({ canEdit }: Props) => {
  const { id: studyId } = useParams();
  const [data, setData] = useState<any[]>([]);

  const [category, setCategory] = useState<string>("");
  const [status, setStatus] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(true);
  const [submitLoader, setSubmitLoader] = useState<string>("");
  const [toggleLoader, setToggleLoader] = useState<boolean>(false);

  const [displayFilterModal, setDisplayFilterModal] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let url = `/platform/study/${studyId}/features`;
        if (category && status) {
          url += `?category=${category}&status=${status}`;
        } else {
          if (category) {
            url += `?category=${category}`;
          }
          if (status) {
            url += "?status=" + status;
          }
        }
        const res: AxiosResponse = await http.get(url);
        const featuresData = res?.data?.data?.rows;
        const newData = featuresData?.map((feature: any) => ({
          id: feature?.id,
          name: feature?.name,
          expanded: false,
          selected: feature?.isEnabled
            ? "yes"
            : computeSelectedState(feature?.childrenFeatures),
          subItems: feature?.childrenFeatures?.map((subFeature: any) => ({
            id: subFeature?.id,
            name: subFeature?.name,
            isEnabled: subFeature?.isEnabled,
          })),
        }));
        setData(newData);

        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchData();
  }, [studyId, toggleLoader, setLoading, category, status]);

  const handleExpandToggle = (id: string) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.id === id ? { ...item, expanded: !item.expanded } : item
      )
    );
  };

  const refreshPage = () => {
    setToggleLoader((prev) => !prev);
  };

  const toggleFeature = async (featureId: string, isEnabled: boolean) => {
    try {
      setSubmitLoader(featureId);
      const body = {
        featureId: featureId,
      };
      const res = await http.post(
        `/platform/study/${studyId}/features/${
          isEnabled ? "disable" : "enable"
        }`,
        body
      );
      toastMessage("success", res?.data?.message);
      setSubmitLoader("");
      refreshPage();
    } catch (error) {
      errorToastMessage(error as Error);
      setSubmitLoader("");
    }
  };

  const openFilterModal = () => {
    setDisplayFilterModal(true);
  };
  const closeFilterModal = () => {
    setDisplayFilterModal(false);
  };

  return (
    <Box>
      <Box sx={SearchBox}>
        <Box sx={{ ...filterBox, width: "70%" }} onClick={openFilterModal}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <SortRounded />
            <Typography>Filter</Typography>
          </Box>
          <ExpandMoreRounded />
        </Box>
      </Box>
      <Table sx={TableBorderRadiusTopLeftRight}>
        <TableHead>
          <TableRow>
            <StyledTableCell
              sx={{
                width: "400px",
              }}
            ></StyledTableCell>
            <StyledTableCell>Applicable</StyledTableCell>
          </TableRow>
        </TableHead>
        {!loading && data?.length > 0 && (
          <>
            <TableBody>
              {data?.map((row: any) => (
                <Fragment key={row?.id}>
                  <TableRow>
                    <StyledTableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <IconButton onClick={() => handleExpandToggle(row?.id)}>
                          {row?.expanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                        <Typography
                          fontSize={16}
                          fontWeight={600}
                          color={"primary.main"}
                        >
                          {row?.name}
                        </Typography>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>
                      {submitLoader === row?.id ? (
                        <CircularProgress size={18} sx={{ ml: 1 }} />
                      ) : (
                        <Checkbox
                          checked={row?.selected === "yes"}
                          indeterminate={row?.selected === "partial"}
                          icon={<CheckBoxOutlineBlank />}
                          onChange={() =>
                            toggleFeature(row?.id, row?.selected === "yes")
                          }
                          checkedIcon={<CheckBox />}
                        />
                      )}
                    </StyledTableCell>
                  </TableRow>
                  {row?.expanded &&
                    row?.subItems?.map((subItem: any) => (
                      <TableRow key={subItem.id}>
                        <StyledTableCell>
                          <Typography
                            fontSize={16}
                            fontWeight={400}
                            sx={{ ml: 8 }}
                          >
                            {subItem.name}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          {submitLoader === subItem.id ? (
                            <CircularProgress size={18} sx={{ ml: 1 }} />
                          ) : (
                            <Checkbox
                              checked={subItem.isEnabled}
                              icon={<CheckBoxOutlineBlank />}
                              onChange={() =>
                                toggleFeature(subItem.id, subItem.isEnabled)
                              }
                              checkedIcon={<CheckBox />}
                            />
                          )}
                        </StyledTableCell>
                      </TableRow>
                    ))}
                </Fragment>
              ))}
            </TableBody>
          </>
        )}
      </Table>
      {!loading && data?.length === 0 && (
        <NoDataContainer>
          <Typography variant="body1" color="gray">
            No Data
          </Typography>
        </NoDataContainer>
      )}
      {loading && (
        <Box sx={{ width: "100%" }}>
          <LinearProgress />
        </Box>
      )}
      {displayFilterModal && (
        <FeaturesFilterModal
          category={category}
          setCategory={setCategory}
          status={status}
          setStatus={setStatus}
          showModal={displayFilterModal}
          closeModal={closeFilterModal}
          refreshPage={refreshPage}
        />
      )}
    </Box>
  );
};

export default PlatformFeaturesList;
